<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="wq-header">
        <span class="wq-bar wq-bar--xl"></span>
        <span class="wq-bar wq-bar--md"></span>
      </div>
      <div class="wq-chain">
        <template v-for="(name, i) in names">
          <div
            :key="name"
            class="wq-node"
            :class="{ '--active': i === activeIndex, '--done': i < activeIndex }"
            :style="{ '--i': i }"
          >
            <span class="wq-avatar">{{ name.charAt(0) }}</span>
            <span class="wq-name">{{ name }}</span>
          </div>
          <span v-if="i < names.length - 1" :key="`arr-${i}`" class="wq-arrow"></span>
        </template>
      </div>
      <div class="wq-active-card">
        <span class="material-symbols-rounded wq-mic">mic</span>
        <span class="wq-bar wq-bar--lg wq-bar--accent"></span>
      </div>
    </template>

    <template #audience>
      <div class="wq-phone-turn">
        <span class="wq-phone-pill">Your turn</span>
      </div>
      <span class="wq-phone-q wq-phone-q--lg"></span>
      <span class="wq-phone-q"></span>
      <div class="wq-phone-input">
        <span class="wq-bar wq-bar--md"></span>
      </div>
      <button type="button" class="wq-phone-send" tabindex="-1">
        Pass
        <span class="material-symbols-rounded wq-send-icon">arrow_forward</span>
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewWalkingQuestion',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      names: ['Ana', 'Sam', 'Mei', 'Tom'],
      activeIndex: 1,
    }
  },
})
</script>

<style scoped lang="scss">
.wq-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.wq-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); }
.wq-bar--lg { width: 60%; }
.wq-bar--md { width: 40%; }
.wq-bar--accent { background: var(--accent); }

.wq-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.wq-chain {
  display: flex; align-items: center; gap: 4px;
  flex-wrap: nowrap;
  margin: 6px 0;
}
.wq-node {
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  flex-shrink: 0;
  opacity: 0.5;
  transition: opacity 0.3s;
  animation: wq-step 4s steps(4) infinite;
  animation-delay: calc(var(--i) * 1s);
}
.wq-avatar {
  width: 26px; height: 26px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
  color: rgba(15, 15, 20, 0.55);
  display: grid; place-items: center;
  font-size: 11px; font-weight: 700;
}
.wq-name {
  font-size: 9px; font-weight: 600;
  color: rgba(15, 15, 20, 0.55);
}
.wq-node.--active {
  opacity: 1;
}
.wq-node.--active .wq-avatar {
  background: var(--accent);
  color: white;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 25%, transparent);
}
.wq-node.--active .wq-name { color: var(--accent); }
.wq-node.--done .wq-avatar {
  background: color-mix(in srgb, var(--accent) 20%, white);
  color: var(--accent);
}
.wq-arrow {
  flex: 1;
  height: 2px;
  background: repeating-linear-gradient(to right, rgba(15, 15, 20, 0.2) 0 3px, transparent 3px 6px);
}

.wq-active-card {
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: center;
  gap: 8px;
  padding: 9px 11px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--accent) 10%, white);
  border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent);
}
.wq-mic {
  width: 24px; height: 24px;
  border-radius: 999px;
  background: var(--accent);
  color: white;
  display: grid; place-items: center;
  font-size: 14px;
}

/* phone */
.wq-phone-turn { margin-bottom: 4px; }
.wq-phone-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--accent);
  color: white;
  font-size: 10px;
  font-weight: 700;
  animation: wq-pulse 2s ease-in-out infinite;
}
.wq-phone-q {
  display: block; height: 7px; width: 60%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.15);
}
.wq-phone-q--lg { width: 80%; background: rgba(15, 15, 20, 0.22); }
.wq-phone-input {
  min-height: 36px;
  border-radius: 6px;
  border: 1px solid rgba(15, 15, 20, 0.15);
  padding: 8px;
  display: flex; align-items: flex-start;
}
.wq-phone-send {
  appearance: none;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  padding: 7px 10px;
  border-radius: 6px;
  background: var(--accent);
  color: white;
  border: none;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.wq-send-icon { font-size: 14px; }

@keyframes wq-step {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.04); }
}
@keyframes wq-pulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
}

@media (prefers-reduced-motion: reduce) {
  .wq-node, .wq-phone-pill { animation: none; }
}
</style>
